import { useState, useMemo, useEffect, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader, LoadingState } from '@v2/design-system';
import { PiMagnifyingGlassBold, PiWarningBold, PiCheckCircleBold, PiCircleBold, PiArrowSquareOutBold, PiDatabase, PiArrowsClockwiseBold, PiFilePdfBold, PiReceiptBold, PiChatCircleTextBold, PiUsersThreeBold, PiBuildingsBold, PiListBulletsBold, PiHandshakeBold, PiPackageBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import api from '@v2/lib/api';
import DealOwnerCell from '@v2/features/ploomes-deals/DealOwnerCell';
import PaymentMethodBadge, { type PaymentMethodKind } from '@v2/features/ploomes-deals/PaymentMethodBadge';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';

// ─── tipos ────────────────────────────────────────────────────────────────────

type ModelField = {
    label: string;
    key: string;
    value: unknown;
    kind?: string | null;
    field_key?: string;
    source?: 'ploomes' | 'local';
};

type TeamAgreementShare = {
    usuario_id?: number;
    name?: string | null;
    avatar_url?: string | null;
    percentual?: number;
};

type OtherPropRow = {
    field_key: string;
    field_id: number;
    label: string | null;
    value: unknown;
    value_type: string;
    raw: Record<string, unknown>;
};

type Document = {
    id: number; name: string; number: number; date: string | null;
    contact_name: string | null; file_name: string | null; url: string | null;
    shared: boolean; externally_accepted: boolean | null; last_opened: string | null; created_at: string;
    other_properties: OtherPropRow[];
};

type Quote = {
    id: number; amount: number; discount: number; externally_accepted: boolean | null;
    shared: boolean; contact_name: string | null; file_name: string | null; url: string | null;
    commission: string | null; commission_amount: string | null; created_at: string; last_updated: string | null;
    other_properties: OtherPropRow[];
};

type DealProduct = {
    product_id: number | null;
    name: string | null;
    code: string | null;
    quantity: number | null;
    unit_price: number | null;
    total: number | null;
    source: 'quote' | 'order' | string;
    source_id: number | null;
    category: unknown;
    other_properties: OtherPropRow[];
};

type Interaction = {
    id: number; date: string | null; type: string; type_id: number;
    title: string | null; content: string | null; subject: string | null;
};

type CollaboratingUser = {
    id: number | null;
    deal_id: number | null;
    user_id: number;
    name: string | null;
    email: string | null;
    is_owner: boolean;
    source: 'ploomes' | 'local' | string;
};

type InspectorCounts = {
    model_fields: number;
    other_properties: number;
    other_properties_filled: number;
    contact_fields: number;
    contact_other_properties: number;
    contact_other_properties_filled: number;
    collaborating_users: number;
    documents: number;
    quotes: number;
    products: number;
    interactions: number;
};

type InspectorData = {
    deal: { id: number; url_key?: string; deal_number: string; internal_number?: string | null; title: string; owner_name: string; ploomes_id: number };
    model_fields: ModelField[];
    other_properties: OtherPropRow[];
    contact_fields?: ModelField[];
    contact_other_properties?: OtherPropRow[];
    documents: Document[];
    quotes: Quote[];
    products?: DealProduct[];
    interactions: Interaction[];
    collaborating_users: CollaboratingUser[];
    field_map_count: number;
    counts?: InspectorCounts;
};

type InspectorTab =
    | 'model'
    | 'deal'
    | 'client'
    | 'collaborators'
    | 'documents'
    | 'quotes'
    | 'products'
    | 'interactions';

const PRODUCT_FIELD_CATEGORY = 'product_733097C4-0D36-4C25-B5B1-3CA7F276EB8B';

// ─── helpers ──────────────────────────────────────────────────────────────────

const TYPE_BADGE: Record<string, string> = {
    bool:       'bg-violet-100 text-violet-700',
    datetime:   'bg-blue-100 text-blue-700',
    decimal:    'bg-emerald-100 text-emerald-700',
    integer:    'bg-cyan-100 text-cyan-700',
    select:     'bg-amber-100 text-amber-700',
    string:     'bg-gray-100 text-gray-600',
    text:       'bg-gray-100 text-gray-600',
    contact:    'bg-pink-100 text-pink-700',
    user:       'bg-indigo-100 text-indigo-700',
    attachment: 'bg-orange-100 text-orange-700',
    empty:      'bg-gray-50 text-gray-400',
};

function formatValue(value: unknown, type: string): string {
    if (value === null || value === undefined) return '—';
    if (type === 'bool') return value ? 'Sim ✓' : 'Não ✗';
    if (type === 'user' || type === 'contact') {
        if (typeof value === 'object' && value !== null) {
            const row = value as { name?: string | null; id?: number | null };
            const name = row.name?.trim();
            if (name && row.id) return `${name} (ID ${row.id})`;
            if (name) return name;
            if (row.id) return `ID ${row.id}`;
        }
    }
    if (type === 'datetime') {
        try {
            return new Date(value as string).toLocaleString('pt-BR');
        } catch {
            return String(value);
        }
    }
    if (typeof value === 'number') {
        return value.toLocaleString('pt-BR');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

function isSdrOwnerValue(value: unknown): value is { id?: number | null; name?: string | null; avatar_url?: string | null } {
    return Boolean(
        value
        && typeof value === 'object'
        && !Array.isArray(value)
        && ('name' in value || 'id' in value)
        && !('percentual' in value),
    );
}

function SdrOwnerValue({ owner }: { owner: { id?: number | null; name?: string | null; avatar_url?: string | null } }) {
    const label = owner.name?.trim() || (owner.id ? `User #${owner.id}` : '—');

    return (
        <div className="flex items-center gap-3">
            <DealOwnerCell name={label} avatarUrl={owner.avatar_url} showName={false} />
            <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{owner.name || '—'}</p>
                <p className="text-[11px] text-gray-500">
                    Ploomes UserId {owner.id ?? '—'}
                </p>
            </div>
        </div>
    );
}

const TEAM_AGREEMENT_BAR_COLORS = [
    'bg-orange-500',
    'bg-sky-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-amber-500',
    'bg-rose-500',
];

function isTeamAgreementShares(value: unknown): value is TeamAgreementShare[] {
    return Array.isArray(value)
        && value.length > 0
        && value.every((row) => row && typeof row === 'object' && ('percentual' in row || 'usuario_id' in row || 'name' in row));
}

function TeamAgreementValue({ shares }: { shares: TeamAgreementShare[] }) {
    const total = shares.reduce((sum, row) => sum + (Number(row.percentual) || 0), 0);

    return (
        <div className="w-full max-w-xl space-y-3">
            <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200/80">
                {shares.map((row, index) => {
                    const pct = Number(row.percentual) || 0;
                    if (pct <= 0) return null;
                    return (
                        <div
                            key={`${row.usuario_id ?? 'u'}-${index}-bar`}
                            className={`h-full ${TEAM_AGREEMENT_BAR_COLORS[index % TEAM_AGREEMENT_BAR_COLORS.length]}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                            title={`${row.name ?? `User #${row.usuario_id}`}: ${pct}%`}
                        />
                    );
                })}
            </div>

            <div className="space-y-2">
                {shares.map((row, index) => {
                    const pct = Number(row.percentual) || 0;
                    const color = TEAM_AGREEMENT_BAR_COLORS[index % TEAM_AGREEMENT_BAR_COLORS.length];
                    const label = row.name ?? (row.usuario_id ? `User #${row.usuario_id}` : '—');

                    return (
                        <div
                            key={`${row.usuario_id ?? 'u'}-${index}`}
                            className="rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-2.5"
                        >
                            <div className="flex items-center gap-3">
                                <DealOwnerCell name={label} avatarUrl={row.avatar_url} showName={false} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-medium text-gray-900">
                                                {row.name ?? 'Usuário não encontrado'}
                                            </p>
                                            <p className="text-[11px] text-gray-500">
                                                Ploomes UserId {row.usuario_id ?? '—'}
                                            </p>
                                        </div>
                                        <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                                            {pct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
                                        </span>
                                    </div>
                                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
                                        <div
                                            className={`h-full rounded-full ${color} transition-[width]`}
                                            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <p className={`text-[11px] font-medium tabular-nums ${Math.abs(total - 100) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`}>
                Total rateio: {total.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
                {Math.abs(total - 100) >= 0.01 ? ' (diferente de 100%)' : ''}
            </p>
        </div>
    );
}

function formatModelFieldValue(value: unknown): { text: string; isJson: boolean } {
    if (value === null || value === undefined || value === '') {
        return { text: '—', isJson: false };
    }
    if (typeof value === 'boolean') {
        return { text: value ? 'Sim ✓' : 'Não ✗', isJson: false };
    }
    if (typeof value === 'object') {
        return { text: JSON.stringify(value, null, 2), isJson: true };
    }
    return { text: String(value), isJson: false };
}

function ModelFieldsTable({ fields, title }: { fields: ModelField[]; title?: string }) {
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-2.5 dark:border-zinc-800 dark:bg-zinc-800/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                    {title ?? 'Campos do modelo local (tabela `ploomes_deals`)'}
                    <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300">
                        {fields.length}
                    </span>
                </p>
            </div>
            <div className="grid grid-cols-1 divide-y divide-gray-50">
                {fields.map((f) => {
                    const empty = f.value === null || f.value === undefined || f.value === '';
                    const teamShares = f.key === 'team_agreement' && isTeamAgreementShares(f.value) ? f.value : null;
                    const sdrOwner = f.key === 'sdr_owner_name' && isSdrOwnerValue(f.value) ? f.value : null;
                    const isPaymentMethod =
                        f.key === 'entry_payment_method' || f.key === 'remaining_payment_method';
                    const formatted = teamShares || sdrOwner || isPaymentMethod ? null : formatModelFieldValue(f.value);

                    return (
                        <div
                            key={f.key}
                            className={`grid grid-cols-[200px_180px_1fr] items-start gap-2 px-4 py-3 text-sm transition-colors hover:bg-gray-50/60 ${f.source === 'ploomes' ? 'border-l-2 border-orange-400 bg-orange-50/40' : ''}`}
                        >
                            <span className="font-medium text-gray-700">{f.label}</span>
                            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{f.key}</code>
                            <span className="flex items-start justify-between gap-2">
                                {teamShares ? (
                                    <TeamAgreementValue shares={teamShares} />
                                ) : sdrOwner ? (
                                    <SdrOwnerValue owner={sdrOwner} />
                                ) : isPaymentMethod ? (
                                    <PaymentMethodBadge
                                        label={typeof f.value === 'string' ? f.value : null}
                                        kind={(f.kind as PaymentMethodKind) ?? undefined}
                                    />
                                ) : formatted?.isJson ? (
                                    <pre className="max-w-full overflow-x-auto rounded-lg bg-gray-900 px-3 py-2 text-[11px] leading-relaxed text-gray-100">
                                        {formatted.text}
                                    </pre>
                                ) : (
                                    <span className={`break-all ${empty ? 'text-gray-300' : 'text-gray-900'}`}>
                                        {formatted?.text}
                                    </span>
                                )}
                                {f.source === 'ploomes' && (
                                    <span className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700">
                                        Ploomes
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── componentes ──────────────────────────────────────────────────────────────

function DealIdInput({ onSearch }: { onSearch: (id: string) => void }) {
    const [input, setInput] = useState('');

    return (
        <div className="flex gap-2">
            <input
                type="text"
                placeholder="ID local do deal (ex: 668)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && input && onSearch(input)}
                className="w-64 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
            <button
                onClick={() => input && onSearch(input)}
                disabled={!input}
                className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
            >
                <PiMagnifyingGlassBold className="h-4 w-4" />
                Inspecionar
            </button>
        </div>
    );
}

/** Tabela compacta de OtherProperties para uso dentro de documentos e quotes */
function InlinePropsTable({ rows, title }: { rows: OtherPropRow[]; title?: string }) {
    const [search, setSearch] = useState('');
    const [onlyFilled, setOnlyFilled] = useState(true);

    const filtered = useMemo(() => {
        let list = onlyFilled ? rows.filter((r) => r.value_type !== 'empty') : rows;
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(
                (r) => r.label?.toLowerCase().includes(q) || r.field_key?.toLowerCase().includes(q) || String(r.value ?? '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [rows, search, onlyFilled]);

    const filledCount = rows.filter((r) => r.value_type !== 'empty').length;

    return (
        <div className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/60">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs">
                <span className="font-semibold text-gray-500">
                    {title ?? 'Campos customizados'}
                    <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-orange-700">{filledCount}/{rows.length}</span>
                </span>
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        placeholder="Buscar…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs focus:border-orange-300 focus:outline-none w-36"
                    />
                    <button
                        onClick={() => setOnlyFilled((v) => !v)}
                        className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${onlyFilled ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                        {onlyFilled ? 'Com valor' : 'Todos'}
                    </button>
                </div>
            </div>
            {filtered.length === 0 ? (
                <p className="px-3 pb-3 text-xs text-gray-400">{onlyFilled ? 'Nenhum campo preenchido.' : 'Nenhum campo.'}</p>
            ) : (
                <div className="divide-y divide-gray-100">
                    {filtered.map((row) => (
                        <div key={row.field_key} className="grid grid-cols-[180px_1fr_80px] items-start gap-2 px-3 py-1.5 text-xs hover:bg-white/60">
                            <span className="font-medium text-gray-700 truncate" title={row.label ?? undefined}>
                                {row.label ?? <span className="text-rose-400">Sem label</span>}
                            </span>
                            <span className={row.value_type === 'empty' ? 'text-gray-300' : 'break-all text-gray-800'}>
                                {row.value_type === 'empty' ? '—' : formatValue(row.value, row.value_type)}
                            </span>
                            <span className={`rounded-full px-1.5 py-0.5 text-center text-[10px] font-medium ${TYPE_BADGE[row.value_type] ?? 'bg-gray-100 text-gray-500'}`}>
                                {row.value_type}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SectionHeader({ icon, title, count }: { icon: React.ReactNode; title: string; count: number }) {
    return (
        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
            <span className="text-gray-500">{icon}</span>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {title}
                <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">{count}</span>
            </p>
        </div>
    );
}

function DocumentsTable({ docs }: { docs: Document[] }) {
    const [expanded, setExpanded] = useState<number | null>(null);
    if (!docs.length) return null;

    const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <SectionHeader icon={<PiFilePdfBold className="h-4 w-4" />} title="Documentos / Propostas" count={docs.length} />
            <div className="divide-y divide-gray-50">
                {docs.map((d) => (
                    <div key={d.id} className="px-4 py-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-xs text-gray-400">#{d.number}</span>
                                <div>
                                    <p className="font-medium text-gray-800">{d.name}</p>
                                    <p className="text-xs text-gray-500">{d.contact_name ?? '—'} · {fmt(d.date)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {d.externally_accepted === true
                                    ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Aceito</span>
                                    : d.externally_accepted === false
                                        ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">Recusado</span>
                                        : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Pendente</span>}
                                {d.last_opened && <span className="text-xs text-gray-400">Último acesso: {fmt(d.last_opened)}</span>}
                                {d.url && (
                                    <a href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                                        <PiArrowSquareOutBold className="h-3.5 w-3.5" /> PDF
                                    </a>
                                )}
                                <button
                                    onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                                    className="text-xs text-violet-600 hover:underline"
                                >
                                    {expanded === d.id ? 'ocultar campos' : `campos (${d.other_properties.filter(r => r.value_type !== 'empty').length}/${d.other_properties.length})`}
                                </button>
                            </div>
                        </div>
                        {expanded === d.id && (
                            <InlinePropsTable rows={d.other_properties} title="Campos customizados do documento" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProductsTable({ products }: { products: DealProduct[] }) {
    const [expanded, setExpanded] = useState<string | null>(null);
    if (!products.length) return null;

    const brl = (v: number | string | null | undefined) => {
        const n = typeof v === 'string' ? parseFloat(v) : v;
        return n != null && !Number.isNaN(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
    };

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <SectionHeader icon={<PiPackageBold className="h-4 w-4" />} title="Produtos do deal" count={products.length} />
            <div className="divide-y divide-gray-50 dark:divide-zinc-800">
                {products.map((p, idx) => {
                    const rowKey = `${p.product_id ?? 'x'}-${p.source_id ?? idx}`;
                    const categoryRow = p.other_properties.find((r) => r.field_key === PRODUCT_FIELD_CATEGORY);
                    const categoryLabel = formatValue(p.category ?? categoryRow?.value, categoryRow?.value_type ?? 'string');

                    return (
                        <div key={rowKey} className="px-4 py-3">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-semibold text-gray-900 dark:text-zinc-100">{p.name ?? '—'}</p>
                                        {p.code && (
                                            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 dark:bg-zinc-800 dark:text-zinc-300">
                                                {p.code}
                                            </span>
                                        )}
                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                                            Categoria: {categoryLabel}
                                        </span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-zinc-400">
                                        <span>ProductId {p.product_id ?? '—'}</span>
                                        <span>Qtd {p.quantity ?? '—'}</span>
                                        <span>{brl(p.unit_price)}</span>
                                        <span className="font-medium text-gray-700 dark:text-zinc-300">{brl(p.total)}</span>
                                        <span>
                                            via {p.source}
                                            {p.source_id ? ` #${p.source_id}` : ''}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setExpanded(expanded === rowKey ? null : rowKey)}
                                    className="text-xs text-violet-600 hover:underline dark:text-violet-400"
                                >
                                    {expanded === rowKey
                                        ? 'ocultar campos'
                                        : `campos (${p.other_properties.filter((r) => r.value_type !== 'empty').length}/${p.other_properties.length})`}
                                </button>
                            </div>
                            {expanded === rowKey && (
                                <InlinePropsTable rows={p.other_properties} title="Campos customizados do produto (catálogo)" />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function QuotesTable({ quotes }: { quotes: Quote[] }) {
    const [expanded, setExpanded] = useState<number | null>(null);
    if (!quotes.length) return null;

    const brl = (v: number | string | null | undefined) => {
        const n = typeof v === 'string' ? parseFloat(v) : v;
        return n != null && !isNaN(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
    };
    const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <SectionHeader icon={<PiReceiptBold className="h-4 w-4" />} title="Cotações / Pedidos" count={quotes.length} />
            <div className="divide-y divide-gray-50">
                {quotes.map((q) => (
                    <div key={q.id} className="px-4 py-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex items-center gap-4">
                                <span className="font-mono text-xs text-gray-400">{q.id}</span>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-sm sm:grid-cols-4">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Valor</p>
                                        <p className="font-semibold text-gray-900">{brl(q.amount)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Desconto</p>
                                        <p className="text-rose-700">{q.discount ? `−${q.discount.toFixed(2)}%` : '0%'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Comissão %</p>
                                        <p className="text-gray-700">{q.commission || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Comissão R$</p>
                                        <p className="font-medium text-emerald-700">{brl(q.commission_amount)}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{fmt(q.created_at)}</span>
                                {q.externally_accepted === true
                                    ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Aceito</span>
                                    : q.externally_accepted === false
                                        ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">Recusado</span>
                                        : null}
                                {q.url && (
                                    <a href={q.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                                        <PiArrowSquareOutBold className="h-3.5 w-3.5" /> PDF
                                    </a>
                                )}
                                <button
                                    onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                                    className="text-xs text-violet-600 hover:underline"
                                >
                                    {expanded === q.id ? 'ocultar campos' : `campos (${q.other_properties.filter(r => r.value_type !== 'empty').length}/${q.other_properties.length})`}
                                </button>
                            </div>
                        </div>
                        {expanded === q.id && (
                            <InlinePropsTable rows={q.other_properties} title="Campos customizados da cotação" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function InteractionsTable({ interactions }: { interactions: Interaction[] }) {
    if (!interactions.length) return null;

    const [expanded, setExpanded] = useState<number | null>(null);

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <SectionHeader icon={<PiChatCircleTextBold className="h-4 w-4" />} title="Histórico de Interações" count={interactions.length} />
            <div className="divide-y divide-gray-50">
                {interactions.map((i) => (
                    <div key={i.id} className="px-4 py-3 hover:bg-gray-50/60">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">{i.type}</span>
                                <span className="text-xs text-gray-500">{i.date ?? '—'}</span>
                                {i.subject && <span className="text-xs font-medium text-gray-700">{i.subject}</span>}
                                {i.title && <span className="text-xs text-gray-600">{i.title}</span>}
                            </div>
                            {i.content && (
                                <button onClick={() => setExpanded(expanded === i.id ? null : i.id)} className="shrink-0 text-xs text-blue-600 hover:underline">
                                    {expanded === i.id ? 'ocultar' : 'ver conteúdo'}
                                </button>
                            )}
                        </div>
                        {expanded === i.id && i.content && (
                            <div className="mt-2 rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">
                                {i.content}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function CollaboratingUsersTable({ users }: { users: CollaboratingUser[] }) {
    const source = users[0]?.source ?? 'ploomes';

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <span className="text-gray-500"><PiUsersThreeBold className="h-4 w-4" /></span>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        CollaboratingUsers
                        <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">{users.length}</span>
                    </p>
                </div>
                <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                    {source === 'ploomes' ? 'API Ploomes ($expand)' : 'cache local'}
                </span>
            </div>
            {users.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-400">
                    Nenhum colaborador neste deal. A relação OData <code className="rounded bg-gray-100 px-1">CollaboratingUsers</code> veio vazia.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50/80 text-left text-[11px] uppercase tracking-wide text-gray-500">
                            <tr>
                                <th className="px-4 py-2 font-semibold">UserId</th>
                                <th className="px-4 py-2 font-semibold">Nome</th>
                                <th className="px-4 py-2 font-semibold">E-mail</th>
                                <th className="px-4 py-2 font-semibold">Papel</th>
                                <th className="px-4 py-2 font-semibold">Id (relação)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {users.map((u) => (
                                <tr key={`${u.id ?? 'x'}-${u.user_id}`} className="hover:bg-gray-50/60">
                                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{u.user_id}</td>
                                    <td className="px-4 py-2 font-medium text-gray-900">{u.name ?? '—'}</td>
                                    <td className="px-4 py-2 text-xs text-gray-500">{u.email ?? '—'}</td>
                                    <td className="px-4 py-2">
                                        {u.is_owner ? (
                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                Owner + colaborador
                                            </span>
                                        ) : (
                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                                Colaborador
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 font-mono text-xs text-gray-400">{u.id ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function OtherPropsTable({
    rows,
    title = 'Campos other_properties',
    codeHint,
}: {
    rows: OtherPropRow[];
    title?: string;
    codeHint?: string;
}) {
    const [search, setSearch] = useState('');
    const [showEmptyOnly, setShowEmptyOnly] = useState<'all' | 'filled' | 'empty'>('all');
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    const filtered = useMemo(() => {
        let list = rows;
        if (showEmptyOnly === 'filled') list = list.filter((r) => r.value_type !== 'empty');
        if (showEmptyOnly === 'empty')  list = list.filter((r) => r.value_type === 'empty');
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(
                (r) =>
                    r.field_key?.toLowerCase().includes(q) ||
                    r.label?.toLowerCase().includes(q) ||
                    String(r.value ?? '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [rows, search, showEmptyOnly]);

    const filledCount = rows.filter((r) => r.value_type !== 'empty').length;

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            {/* Cabeçalho + filtros */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-800/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                    {title}
                    {codeHint && (
                        <code className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-normal normal-case text-gray-500 dark:bg-zinc-700 dark:text-zinc-300">
                            {codeHint}
                        </code>
                    )}
                    <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300">
                        {filledCount}/{rows.length} preenchidos
                    </span>
                </p>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <PiMagnifyingGlassBold className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar label, chave ou valor…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-xs focus:border-orange-400 focus:outline-none"
                        />
                    </div>
                    <div className="flex rounded-lg border border-gray-200 text-xs">
                        {(['all', 'filled', 'empty'] as const).map((opt) => (
                            <button
                                key={opt}
                                onClick={() => setShowEmptyOnly(opt)}
                                className={`px-2.5 py-1.5 transition-colors first:rounded-l-lg last:rounded-r-lg ${showEmptyOnly === opt ? 'bg-orange-500 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                            >
                                {opt === 'all' ? 'Todos' : opt === 'filled' ? 'Com valor' : 'Vazios'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tabela */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50/80 text-xs">
                        <tr>
                            <th className="py-2 pl-4 pr-2 text-left font-semibold text-gray-500">Label (Ploomes)</th>
                            <th className="py-2 px-2 text-left font-semibold text-gray-500">Field Key</th>
                            <th className="py-2 px-2 text-left font-semibold text-gray-500">Tipo</th>
                            <th className="py-2 px-2 text-left font-semibold text-gray-500">Valor</th>
                            <th className="py-2 pr-4 text-center font-semibold text-gray-500">Raw</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {filtered.map((row, index) => {
                            const rowKey = row.field_key ?? `field-${row.field_id ?? index}`;
                            const isEmpty = row.value_type === 'empty';
                            const isExpanded = expandedKey === rowKey;

                            return (
                                <Fragment key={rowKey}>
                                    <tr
                                        className={`group transition-colors hover:bg-orange-50/40 ${isEmpty ? 'opacity-40' : ''}`}
                                    >
                                        <td className="py-2 pl-4 pr-2">
                                            {row.label
                                                ? <span className="font-medium text-gray-800">{row.label}</span>
                                                : <span className="flex items-center gap-1 text-rose-500"><PiWarningBold className="h-3.5 w-3.5" /> Sem label</span>}
                                        </td>
                                        <td className="py-2 px-2">
                                            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">{row.field_key}</code>
                                        </td>
                                        <td className="py-2 px-2">
                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${TYPE_BADGE[row.value_type] ?? 'bg-gray-100 text-gray-500'}`}>
                                                {row.value_type}
                                            </span>
                                        </td>
                                        <td className="py-2 px-2 max-w-xs">
                                            <span className={isEmpty ? 'text-gray-300' : 'text-gray-900'}>
                                                {formatValue(row.value, row.value_type)}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4 text-center">
                                            <button
                                                onClick={() => setExpandedKey(isExpanded ? null : rowKey)}
                                                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                                title="Ver JSON bruto"
                                            >
                                                {isExpanded ? <PiCheckCircleBold className="h-4 w-4 text-orange-500" /> : <PiCircleBold className="h-4 w-4" />}
                                            </button>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="bg-gray-900/5">
                                            <td colSpan={5} className="px-4 py-3">
                                                <pre className="overflow-x-auto rounded-lg bg-gray-900 p-3 text-[11px] leading-relaxed text-gray-100">
                                                    {JSON.stringify(row.raw, null, 2)}
                                                </pre>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
                {filtered.length === 0 && (
                    <div className="py-10 text-center text-sm text-gray-400">Nenhum campo encontrado para o filtro aplicado.</div>
                )}
            </div>
        </div>
    );
}

// ─── página principal ──────────────────────────────────────────────────────────

export default function PloomesFieldInspectorPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dealId, setDealId] = useState<string | null>(searchParams.get('deal'));
    const queryClient = useQueryClient();

    const tabFromUrl = (searchParams.get('tab') as InspectorTab | null) ?? 'model';
    const [activeTab, setActiveTab] = useState<InspectorTab>(tabFromUrl);

    useEffect(() => {
        const id = searchParams.get('deal');
        if (id) setDealId(id);
        const tab = searchParams.get('tab') as InspectorTab | null;
        if (tab) setActiveTab(tab);
    }, [searchParams]);

    const { data, isLoading, isError, error } = useQuery<InspectorData>({
        queryKey: ['ploomes-field-inspector', dealId],
        queryFn: () =>
            api
                .get(`/commissioning/deals/${dealId}/ploomes-fields`)
                .then((r) => r.data),
        enabled: !!dealId,
        staleTime: 60_000,
    });

    const syncMutation = useMutation({
        // Sync scoped: só este deal (não dispara sync-all / import em massa).
        mutationFn: () => api.post(`/commissioning/deals/${dealId}/sync-from-ploomes`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ploomes-field-inspector', dealId] });
        },
    });

    const contactFields = data?.contact_fields ?? [];
    const contactOtherProps = data?.contact_other_properties ?? [];
    const counts = data?.counts;

    const tabs = useMemo(() => {
        if (!data) return [];

        const dealFilled = counts?.other_properties_filled ?? data.other_properties.filter((r) => r.value_type !== 'empty').length;
        const dealTotal = counts?.other_properties ?? data.other_properties.length;
        const clientCustomFilled = counts?.contact_other_properties_filled
            ?? contactOtherProps.filter((r) => r.value_type !== 'empty').length;
        const clientCustomTotal = counts?.contact_other_properties ?? contactOtherProps.length;
        const clientModel = counts?.contact_fields ?? contactFields.length;
        const clientTotal = clientModel + clientCustomTotal;

        return [
            {
                key: 'model' as const,
                label: 'Modelo',
                icon: PiListBulletsBold,
                count: counts?.model_fields ?? data.model_fields.length,
                hint: 'campos',
            },
            {
                key: 'deal' as const,
                label: 'Deal',
                icon: PiHandshakeBold,
                count: dealTotal,
                filled: dealFilled,
                hint: 'custom',
            },
            {
                key: 'client' as const,
                label: 'Cliente',
                icon: PiBuildingsBold,
                count: clientTotal,
                filled: clientCustomFilled + contactFields.filter((f) => f.value !== null && f.value !== undefined && f.value !== '').length,
                hint: 'campos',
            },
            {
                key: 'products' as const,
                label: 'Produtos',
                icon: PiPackageBold,
                count: counts?.products ?? data.products?.length ?? 0,
                hint: 'itens',
            },
            {
                key: 'collaborators' as const,
                label: 'Colaboradores',
                icon: PiUsersThreeBold,
                count: counts?.collaborating_users ?? data.collaborating_users?.length ?? 0,
                hint: 'users',
            },
            {
                key: 'documents' as const,
                label: 'Documentos',
                icon: PiFilePdfBold,
                count: counts?.documents ?? data.documents.length,
                hint: 'docs',
            },
            {
                key: 'quotes' as const,
                label: 'Cotações',
                icon: PiReceiptBold,
                count: counts?.quotes ?? data.quotes.length,
                hint: 'quotes',
            },
            {
                key: 'interactions' as const,
                label: 'Interações',
                icon: PiChatCircleTextBold,
                count: counts?.interactions ?? data.interactions.length,
                hint: 'regs',
            },
        ];
    }, [data, counts, contactFields, contactOtherProps]);

    function selectTab(tab: InspectorTab) {
        setActiveTab(tab);
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        if (dealId) next.set('deal', dealId);
        setSearchParams(next, { replace: true });
    }

    function handleSearch(id: string) {
        setDealId(id);
        const next = new URLSearchParams(searchParams);
        next.set('deal', id);
        if (!next.get('tab')) next.set('tab', activeTab);
        setSearchParams(next, { replace: true });
    }

    return (
        <div className="space-y-6 p-6">
            <PageHeader
                title="Inspetor de Campos Ploomes"
                description="Visualize todos os campos raw do Ploomes para mapear método de pagamento, valores e demais dados."
                icon={<PiDatabase className="h-5 w-5" />}
            />

            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-zinc-400">ID do deal (local)</p>
                    <DealIdInput onSearch={handleSearch} />
                </div>
                {data && (
                    <>
                        <button
                            onClick={() => syncMutation.mutate()}
                            disabled={syncMutation.isPending}
                            title="Atualiza somente este deal no Hub a partir do Ploomes (não sincroniza os demais)"
                            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-blue-500/40 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
                        >
                            <PiArrowsClockwiseBold className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                            {syncMutation.isPending ? 'Sincronizando este deal…' : 'Sincronizar este deal'}
                        </button>
                        {syncMutation.isSuccess && (
                            <span className="text-xs text-emerald-600">✓ Só este deal foi sincronizado</span>
                        )}
                        {syncMutation.isError && (
                            <span className="text-xs text-rose-600">Erro ao sincronizar este deal</span>
                        )}
                        <a
                            href={ploomesDealPath(data.deal)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline dark:text-blue-400"
                        >
                            <PiArrowSquareOutBold className="h-4 w-4" />
                            Abrir deal #{data.deal.deal_number}
                        </a>
                    </>
                )}
            </div>

            {isLoading && <LoadingState variant="detail" />}

            {isError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-300">
                    Erro ao carregar: {(error as Error)?.message ?? 'Deal não encontrado.'}
                </div>
            )}

            {data && (
                <>
                    <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 sm:grid-cols-4">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">Deal</p>
                            <p className="font-semibold text-gray-900 dark:text-zinc-100">#{data.deal.deal_number}</p>
                        </div>
                        <div className="col-span-2 sm:col-span-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">Título</p>
                            <p className="truncate font-medium text-gray-900 dark:text-zinc-100">{data.deal.title}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">Vendedor</p>
                            <p className="font-medium text-gray-900 dark:text-zinc-100">{data.deal.owner_name}</p>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <div className="inline-flex min-w-full gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-zinc-700 dark:bg-zinc-800/60 sm:min-w-0 sm:flex sm:flex-wrap">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                const active = activeTab === tab.key;
                                const countLabel = tab.filled != null
                                    ? `${tab.filled}/${tab.count}`
                                    : String(tab.count);

                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => selectTab(tab.key)}
                                        className={cn(
                                            'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
                                            active
                                                ? 'bg-white text-orange-700 shadow-sm dark:bg-zinc-900 dark:text-orange-300'
                                                : 'text-gray-600 hover:bg-white/70 hover:text-gray-900 dark:text-zinc-400 dark:hover:bg-zinc-900/60 dark:hover:text-zinc-100',
                                        )}
                                    >
                                        <Icon className="h-3.5 w-3.5 shrink-0" />
                                        {tab.label}
                                        <span
                                            className={cn(
                                                'rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                                                active
                                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300'
                                                    : 'bg-gray-200/80 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300',
                                            )}
                                            title={tab.hint}
                                        >
                                            {countLabel}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {activeTab === 'model' && (
                        <ModelFieldsTable
                            fields={data.model_fields}
                            title="Campos do modelo local (deal)"
                        />
                    )}

                    {activeTab === 'deal' && (
                        <OtherPropsTable
                            rows={data.other_properties}
                            title="Campos customizados do Deal"
                            codeHint="other_properties"
                        />
                    )}

                    {activeTab === 'client' && (
                        <div className="space-y-4">
                            <ModelFieldsTable
                                fields={contactFields}
                                title="Campos do Contact (modelo local)"
                            />
                            <OtherPropsTable
                                rows={contactOtherProps}
                                title="Campos customizados do Cliente"
                                codeHint="contact_other_properties"
                            />
                            {contactFields.length === 0 && contactOtherProps.length === 0 && (
                                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
                                    Nenhum campo de cliente encontrado. Clique em <strong>Sincronizar este deal</strong> para puxar{' '}
                                    <code className="rounded bg-gray-200 px-1 dark:bg-zinc-700">Contact.OtherProperties</code> do Ploomes.
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'collaborators' && (
                        <CollaboratingUsersTable users={data.collaborating_users ?? []} />
                    )}

                    {activeTab === 'documents' && (
                        data.documents.length > 0
                            ? <DocumentsTable docs={data.documents} />
                            : <EmptyTab message="Nenhum documento/proposta neste deal." />
                    )}

                    {activeTab === 'quotes' && (
                        data.quotes.length > 0
                            ? <QuotesTable quotes={data.quotes} />
                            : <EmptyTab message="Nenhuma cotação neste deal." />
                    )}

                    {activeTab === 'products' && (
                        (data.products?.length ?? 0) > 0
                            ? <ProductsTable products={data.products ?? []} />
                            : (
                                <EmptyTab message="Nenhum produto na cotação/pedido deste deal. Campo Categoria (product_733097C4…) aparece nos OtherProperties do catálogo." />
                            )
                    )}

                    {activeTab === 'interactions' && (
                        data.interactions.length > 0
                            ? <InteractionsTable interactions={data.interactions} />
                            : <EmptyTab message="Nenhuma interação neste deal." />
                    )}

                    <p className="text-right text-xs text-gray-400 dark:text-zinc-500">
                        {data.field_map_count} campos mapeados no Ploomes (cache 24h)
                    </p>
                </>
            )}

            {!dealId && !isLoading && (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 py-16 text-center dark:border-zinc-700 dark:bg-zinc-900/40">
                    <PiDatabase className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-zinc-600" />
                    <p className="text-sm text-gray-400">Digite o ID de um deal para inspecionar os campos do Ploomes.</p>
                    <p className="mt-1 text-xs text-gray-400">
                        Campos conhecidos: <code className="rounded bg-gray-200 px-1 dark:bg-zinc-700">deal_F973D…</code> = Formato de pagamento,{' '}
                        <code className="rounded bg-gray-200 px-1 dark:bg-zinc-700">contact_F48B…</code> = Buckler One,{' '}
                        <code className="rounded bg-gray-200 px-1 dark:bg-zinc-700">product_733097…</code> = Categoria
                    </p>
                </div>
            )}
        </div>
    );
}

function EmptyTab({ message }: { message: string }) {
    return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
            {message}
        </div>
    );
}
